import * as React from "react";

import Log, {Errors} from "@khan/logging";

interface ErrorInfo {
    componentStack: string;
}

interface Props {
    children: React.ReactNode;
    /**
     * Handle an error instead of logging it.
     */
    onError?: (
        error?: Error | null | undefined,
        errorInfo?: ErrorInfo | null | undefined,
    ) => void;
    /**
     * Provide a custom error component to render when an error occurs.
     */
    errorFallback: (error?: Error | null | undefined) => React.ReactNode;
    /**
     * Set when we are to directly render an error rather than waiting to
     * catch one. This is used by our server-side rendering to provide a nice
     * error page should a render request error.
     */
    error?: Error | null | undefined;
}

interface State {
    error: Error | null | undefined;
}

/**
 * This component helps us to catch JavaScript errors anywhere in the component
 * tree its instantiated. We use this component to gracefully display an Error
 * UI fallback and, if the error happens during Client-Side rendering, then we
 * report it with our centralized logging framework (which ultimately will
 * log to Sentry).
 *
 * TODO(WEB-2282): Figure out what needs to be done to use this component on SSR
 * and wrap the body of render() function with a try/catch statement.
 *
 * @see https://reactjs.org/docs/error-boundaries.html
 *
 * @example
 * <ErrorBoundary>
 *  <AboutPage>
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<Props, State> {
    static getDerivedStateFromError(error?: Error | null): {
        error: Error | null | undefined;
    } {
        // Update state so the next render will show the fallback UI.
        // Note: This will only work for CSR.
        return {error};
    }

    state: State = {error: null};

    /**
     * This lifecycle is invoked after an error has been thrown by a descendant
     * component.
     * @param {?Error} error The error that was thrown.
     * @param {ErrorInfo} errorInfo An object with a componentStack key
     * containing information about which component threw the error.
     */
    componentDidCatch(error: Error | null | undefined, errorInfo: ErrorInfo) {
        this.logError(error, errorInfo);
    }

    /**
     * Logs error possibly using a custom logger.
     * @param {Error} error The error thrown (optional).
     * @param {ErrorInfo} errorInfo The extra info (CSR).
     */
    logError: (
        error?: Error | null | undefined,
        errorInfo?: ErrorInfo | null | undefined,
    ) => void = (error?: Error | null, errorInfo?: ErrorInfo | null) => {
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
            return;
        }

        Log.error("CSR Error", Errors.Internal, {
            metadata: {
                ...errorInfo,
            },
            cause: error,
        });
    };

    /**
     * Renders the error fallback UI
     */
    renderError: () => React.ReactNode = () => {
        return this.props.errorFallback(this.state.error || this.props.error);
    };

    render(): React.ReactNode {
        if (this.state.error || this.props.error) {
            // render fallback UI during CSR
            return this.renderError();
        }

        return this.props.children;
    }
}
